import type { LedgerBucket, LedgerReason, SimulationLineItem } from '~/store/billing'

export function useBillingLabels() {
  const { t } = useI18n()

  /**
   * Customer-facing addon name, keyed by `addon_type` — see the identical map
   * exposed below, which reads from this one so the two can never disagree.
   */
  const addonTypeLabels = computed<Record<string, string>>(() => ({
    EXTRA_DISK: t('billing.addonType.EXTRA_DISK'),
    MAIN_DISK_EXPANSION: t('billing.addonType.MAIN_DISK_EXPANSION'),
    EXTRA_IPV4: t('billing.addonType.EXTRA_IPV4'),
    EXTRA_IPV6: t('billing.addonType.EXTRA_IPV6'),
    WINDOWS_LICENSE: t('billing.addonType.WINDOWS_LICENSE'),
    CAL_LICENSE: t('billing.addonType.CAL_LICENSE'),
    SNAPSHOT_SLOT: t('billing.addonType.SNAPSHOT_SLOT'),
    BACKUP_SLOT: t('billing.addonType.BACKUP_SLOT'),
    ANTI_DDOS: t('billing.addonType.ANTI_DDOS'),
    PREMIUM_SUPPORT: t('billing.addonType.PREMIUM_SUPPORT'),
  }))

  /**
   * One plain sentence saying what an add-on actually DOES.
   *
   * The names alone are trade vocabulary — "Snapshot slot" and "CAL" tell a
   * customer nothing, and an unexplained line item next to a price is one
   * people skip rather than risk. `catalog_addons.description` exists and was
   * never rendered anywhere, but it is operator-entered pt-BR, so it stays as
   * the FALLBACK for a type this build has no sentence for, exactly like
   * `addonTypeLabels` treats `catalog_addons.name`.
   */
  const addonTypeDescriptions = computed<Record<string, string>>(() => ({
    EXTRA_DISK: t('billing.addonDescription.EXTRA_DISK'),
    MAIN_DISK_EXPANSION: t('billing.addonDescription.MAIN_DISK_EXPANSION'),
    EXTRA_IPV4: t('billing.addonDescription.EXTRA_IPV4'),
    EXTRA_IPV6: t('billing.addonDescription.EXTRA_IPV6'),
    WINDOWS_LICENSE: t('billing.addonDescription.WINDOWS_LICENSE'),
    CAL_LICENSE: t('billing.addonDescription.CAL_LICENSE'),
    SNAPSHOT_SLOT: t('billing.addonDescription.SNAPSHOT_SLOT'),
    BACKUP_SLOT: t('billing.addonDescription.BACKUP_SLOT'),
    ANTI_DDOS: t('billing.addonDescription.ANTI_DDOS'),
    PREMIUM_SUPPORT: t('billing.addonDescription.PREMIUM_SUPPORT'),
  }))

  /**
   * What a priced line of a quote READS as, in the language this console is
   * being used in.
   *
   * `/billing/catalog/simulate` is public and anonymous, so it cannot know
   * that language — it writes one canonical `label` (in pt-BR) and hands over
   * the structured fields the line was built from. Rendering `label` directly
   * is what put "Expansão do disco principal (+20 GB)" in the order summary of
   * an English checkout.
   *
   * The API's label stays the fallback for anything this build cannot
   * describe: an unknown `kind`, an addon type not in the map, or an older API
   * that sends no structured fields at all. It already carries its own
   * quantity suffix, which is why the suffixes below are only ever appended to
   * a name we translated ourselves.
   *
   * `extraDiskIndex` numbers the extra disks the way the customer added them —
   * position among the OTHER extra-disk lines, which is the only thing that
   * tells two 50 GB disks apart.
   */
  function simulationLineLabel(
    li: SimulationLineItem,
    extraDiskIndex = 0
  ): string {
    const count = li.display_quantity

    if (li.kind === 'main_disk_expansion') {
      return count == null
        ? li.label
        : t('billing.lineItem.main_disk_expansion', { gb: count })
    }

    if (li.kind === 'extra_disk') {
      return count == null
        ? li.label
        : t('billing.lineItem.extra_disk', {
            index: extraDiskIndex + 1,
            gb: count,
          })
    }

    if (li.kind === 'compute') return li.label

    const name = li.addon_type ? addonTypeLabels.value[li.addon_type] : undefined
    if (!name) return li.label

    if (li.display_unit === 'vcpu' && count != null) {
      return t('billing.lineItem.perVcpu', { name, count })
    }
    if (li.display_unit === 'unit' && count != null && count > 1) {
      return t('billing.lineItem.quantity', { name, count })
    }
    return name
  }

  const bucketColor: Record<LedgerBucket, string> = {
    COMMITTED: 'info',
    AVAILABLE: 'success',
    TEMPORARY: 'warning',
    NEGATIVE: 'error',
  }

  const invoiceStatusColor: Record<string, string> = {
    OPEN: 'warning',
    PAID: 'success',
    CANCELLED: 'neutral',
    REFUNDED: 'info',
  }

  /**
   * The status a customer should read. A renewal the balance settled is
   * CANCELLED in the database (the cash arrived once, on the top-up, so it is
   * never marked paid) but "Cancelada" is the wrong word for an obligation
   * that was met — every invoice list goes through this rather than the raw
   * status map.
   */
  function invoiceStatusLabelFor(inv: {
    status: string
    settled_by_credit?: boolean
  }): string {
    if (inv.settled_by_credit) return t('billing.invoiceStatus.SETTLED_BY_CREDIT')
    return t(`billing.invoiceStatus.${inv.status}`) || inv.status
  }
  function invoiceStatusColorFor(inv: {
    status: string
    settled_by_credit?: boolean
  }): string {
    if (inv.settled_by_credit) return 'success'
    return invoiceStatusColor[inv.status] ?? 'neutral'
  }

  /**
   * Purpose badge text. Falls back to the raw value rather than hiding the
   * badge: an unlabelled purpose is a missing translation, and showing it is
   * how that gets noticed.
   */
  function invoicePurposeLabel(purpose: string): string {
    const key = `billing.invoicePurpose.${purpose}`
    const label = t(key)
    return label === key ? purpose : label
  }

  function reasonIcon(reason: LedgerReason): string {
    switch (reason) {
      case 'USAGE':
        return 'i-lucide-clock'
      case 'RECHARGE':
        return 'i-lucide-plus-circle'
      case 'TOPUP_COUPON_CREDIT':
      case 'COUPON_DISCOUNT':
        return 'i-lucide-ticket-percent'
      case 'COMMITMENT_PAYMENT':
        return 'i-lucide-lock'
      case 'TEMPORARY_GRANT':
        return 'i-lucide-gift'
      case 'TEMPORARY_EXPIRY':
        return 'i-lucide-hourglass'
      case 'COMMITMENT_EXPIRY':
        return 'i-lucide-lock-open'
      case 'ADJUSTMENT':
        return 'i-lucide-pencil'
      case 'REFUND':
        return 'i-lucide-rotate-ccw'
      case 'PAYMENT_REVERSAL':
        return 'i-lucide-undo-2'
      case 'DEBT_SETTLEMENT':
        return 'i-lucide-scale'
      case 'RENEWAL_CREDIT_APPLIED':
        return 'i-lucide-calendar-check'
      case 'INVOICE_CARRYOVER':
        return 'i-lucide-receipt'
      default:
        return 'i-lucide-arrow-left-right'
    }
  }

  // Wrap in reactive so that nested computed refs are auto-unwrapped on access,
  // letting callers use labels.bucketLabel[key] as if it were a plain Record.
  return reactive({
    bucketLabel: computed<Record<LedgerBucket, string>>(() => ({
      COMMITTED: t('billing.bucket.COMMITTED'),
      AVAILABLE: t('billing.bucket.AVAILABLE'),
      TEMPORARY: t('billing.bucket.TEMPORARY'),
      NEGATIVE: t('billing.bucket.NEGATIVE'),
    })),

    bucketTooltip: computed<Record<LedgerBucket, string>>(() => ({
      COMMITTED: t('billing.bucketTooltip.COMMITTED'),
      AVAILABLE: t('billing.bucketTooltip.AVAILABLE'),
      TEMPORARY: t('billing.bucketTooltip.TEMPORARY'),
      NEGATIVE: t('billing.bucketTooltip.NEGATIVE'),
    })),

    bucketColor,

    reasonLabel: computed<Record<LedgerReason, string>>(() => ({
      USAGE: t('billing.reason.USAGE'),
      RECHARGE: t('billing.reason.RECHARGE'),
      INVOICE_CARRYOVER: t('billing.reason.INVOICE_CARRYOVER'),
      ADJUSTMENT: t('billing.reason.ADJUSTMENT'),
      REFUND: t('billing.reason.REFUND'),
      PAYMENT_REVERSAL: t('billing.reason.PAYMENT_REVERSAL'),
      COMMITMENT_PAYMENT: t('billing.reason.COMMITMENT_PAYMENT'),
      COUPON_DISCOUNT: t('billing.reason.COUPON_DISCOUNT'),
      TOPUP_COUPON_CREDIT: t('billing.reason.TOPUP_COUPON_CREDIT'),
      INTERNAL_TRANSFER_IN: t('billing.reason.INTERNAL_TRANSFER_IN'),
      INTERNAL_TRANSFER_OUT: t('billing.reason.INTERNAL_TRANSFER_OUT'),
      TEMPORARY_GRANT: t('billing.reason.TEMPORARY_GRANT'),
      TEMPORARY_EXPIRY: t('billing.reason.TEMPORARY_EXPIRY'),
      DEBT_SETTLEMENT: t('billing.reason.DEBT_SETTLEMENT'),
      COMMITMENT_EXPIRY: t('billing.reason.COMMITMENT_EXPIRY'),
      RENEWAL_CREDIT_APPLIED: t('billing.reason.RENEWAL_CREDIT_APPLIED'),
      // Withdrawn as a mechanism, kept as a value: two production rows
      // carry it and a ledger that cannot name a reason renders blank.
      CARRY_FORWARD: t('billing.reason.CARRY_FORWARD'),
    })),

    reasonIcon,

    usageKindLabel: computed<Record<string, string>>(() => ({
      compute: t('billing.usageKind.compute'),
      storage: t('billing.usageKind.storage'),
      addon: t('billing.usageKind.addon'),
      ip: t('billing.usageKind.ip'),
      license: t('billing.usageKind.license'),
    })),

    resourceTypeLabel: computed<Record<string, string>>(() => ({
      VM: t('billing.resourceType.VM'),
      DISK_MAIN: t('billing.resourceType.DISK_MAIN'),
      DISK_EXTRA: t('billing.resourceType.DISK_EXTRA'),
      EXTRA_IPV4: t('billing.resourceType.EXTRA_IPV4'),
      EXTRA_IPV6: t('billing.resourceType.EXTRA_IPV6'),
      WINDOWS_LICENSE: t('billing.resourceType.WINDOWS_LICENSE'),
      CAL: t('billing.resourceType.CAL'),
      SNAPSHOT_SLOT: t('billing.resourceType.SNAPSHOT_SLOT'),
      BACKUP_SLOT: t('billing.resourceType.BACKUP_SLOT'),
      VPN_GATEWAY: t('billing.resourceType.VPN_GATEWAY'),
    })),

    invoiceStatusLabel: computed<Record<string, string>>(() => ({
      OPEN: t('billing.invoiceStatus.OPEN'),
      PAID: t('billing.invoiceStatus.PAID'),
      CANCELLED: t('billing.invoiceStatus.CANCELLED'),
      REFUNDED: t('billing.invoiceStatus.REFUNDED'),
    })),

    invoiceStatusColor,
    invoiceStatusLabelFor,
    invoiceStatusColorFor,
    invoicePurposeLabel,

    termLabel: computed<Record<string, string>>(() => ({
      MONTHLY: t('billing.term.MONTHLY'),
      QUARTERLY: t('billing.term.QUARTERLY'),
      SEMI_ANNUAL: t('billing.term.SEMI_ANNUAL'),
      ANNUAL: t('billing.term.ANNUAL'),
      ON_DEMAND: t('billing.term.ON_DEMAND'),
    })),

    /**
     * Customer-facing addon name, keyed by `addon_type`.
     *
     * `catalog_addons.name` is a single operator-entered string — it was
     * seeded in pt-BR, so an English-speaking customer saw "Slot de snapshot"
     * next to translated copy. The TYPE is the stable, enumerable thing, so
     * the label is translated off that and the DB name stays the fallback for
     * a type this build does not know yet.
     */
    addonTypeLabel: addonTypeLabels,

    /**
     * One plain sentence per add-on saying what it does. See the map above for
     * why this is keyed on the type rather than read from the catalog.
     */
    addonTypeDescription: addonTypeDescriptions,

    /** See `simulationLineLabel` above. */
    simulationLineLabel,

    suspensionReasonLabel: computed<Record<string, string>>(() => ({
      tier_threshold: t('billing.suspensionReason.tier_threshold'),
      manual: t('billing.suspensionReason.manual'),
      payment_failed: t('billing.suspensionReason.payment_failed'),
      // The commonest reason of all, and it was missing: the render falls back
      // to `?? s.reason`, so an overdue-suspended customer was shown the raw
      // enum `invoice_overdue`.
      invoice_overdue: t('billing.suspensionReason.invoice_overdue'),
    })),
  })
}
